import dayjs from 'utils/dayjs';
import ObjectUtils from '@spothero/utils/object';
import {catchApiResponse} from 'utils/api';
import {spotheroPost, spotheroPatch} from 'api';
import {sendSentryException} from 'utils/error-handling';

import {displayDateFormat, displayTimeMeridiem} from 'hooks/config';
import {
    EXCEPTION,
    CONTINUOUS,
} from 'pages/inventory/utils/inventory-rule-types';

import {
    NEVER_END,
    SPECIFIC_DATE,
} from 'pages/inventory/components/ExceptionModal/state/exception-modal-state';

/**
 * Transforms the days of the week object from a toggle group to the format expected by the API
 * @private
 * @function transformDaysOfWeek
 *
 * @param {object} daysOfWeek
 * @returns {TDaysOfWeek}
 *
 * @memberof module:api/inventory/dto
 */
export const transformDaysOfWeek = (daysOfWeek) => {
    const keys = Object.keys(daysOfWeek);

    if (keys.length === 0) {
        return;
    }

    return keys.reduce(
        (acc, day) => {
            acc[day] = daysOfWeek[day] === 'on';

            return acc;
        },
        {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false,
        }
    );
};

/**
 * Converts datetime to an ISO string
 * @private
 *
 * @param {string} date - the date that should be formatted
 * @param {string} time - the time that should be formatted
 * @returns {string} - The datetime in ISO format
 *
 * @memberof module:api/inventory/dto
 */
const datetimeToISOString = (date, time) =>
    dayjs(
        `${date} ${time}`,
        `${displayDateFormat} ${displayTimeMeridiem}`
    ).toISOString();

/**
 * Formats a date to the format expected by the API
 *
 * @private
 * @param {string} date - the date that should be formatted
 * @returns {string | undefined}
 *
 * @memberof module:api/inventory/dto
 */
const formatDate = (date) => date?.substr(0, 10);

/**
 * Formats a time to the format expected by the API
 * @private
 *
 * @param {string} time - the time that should be formatted
 * @returns {string} - The time in HH:mm format
 *
 * @memberof module:api/inventory/dto
 */
const formatTime = (time) => dayjs(time, displayTimeMeridiem).format('HH:mm'); // eslint-disable-line padding-line-between-statements

/**
 * Formats a date to a weekday in the format expected by the API
 * @private
 * @function
 *
 * @param {string} date
 * @returns {number}
 *
 * @memberof module:api/inventory/dto
 */
export const toWeekday = (date) =>
    (dayjs(date, `${displayDateFormat}, ddd`).weekday() + 6) % 7;

/**
 * Formats the response for inventory exceptions
 * @param {TInventoryRule} response
 * @return {TInventoryRule}
 *
 * @memberof module:api/inventory/dto
 */
const formatAddInventoryExceptionResponse = (response) => {
    const formattedResponse = ObjectUtils.camelizeKeys(response);

    return formattedResponse;
};

/**
 * Adds an inventory exception for a given facility
 * @param {TInventoryRuleRequest} exception - The exception to add
 * @returns {Promise<TInventoryRule>}
 * @throws {Error}
 *
 * @see '/api/v1/facilities/{facilityId}/inventory-rules/'
 */
const addInventoryException = async ({
    facilityId,
    ruleId,
    title,
    endDate,
    quantity,
    startDate,
    // if this is not sent along, it will be status deleted
    status = 'enabled',
    endTime = '11:59 PM',
    startTime = '12:00 AM',
    scheduleType = EXCEPTION,
    repeatEndType,
    repeatEndDate,
    ...daysOfWeek
}) => {
    try {
        // Todo: refactor this entire function into two separate functions for adding and updating exceptions, as well as adding a request DTO to format the request data.
        const apiMethod = ruleId ? spotheroPatch : spotheroPost;
        const apiEndpoint = ruleId
            ? `facilities/${facilityId}/inventory-rules/${ruleId}/`
            : `facilities/${facilityId}/inventory-rules/`;
        // TODO: DateTimeRangePicker does not mask the display view from the serialized value
        // Update that component to allow for the display value to be seperate from the serialized value
        const formattedStartDate = formatDate(startDate);
        const formattedEndDate = formatDate(endDate);
        let validTo;
        let validToTime;
        let validToDate;
        let validFrom = datetimeToISOString(formattedStartDate, '12:00 AM');
        let validFromTime = '12:00 AM';
        let formattedStartTime = formatTime(startTime);
        let formattedEndTime = formatTime(endTime);

        if (repeatEndType === SPECIFIC_DATE) {
            validToDate = formatDate(repeatEndDate);
            validTo = datetimeToISOString(validToDate, '11:59 PM');
            validToTime = '11:59 PM';
        }

        if (repeatEndType === NEVER_END) {
            validTo = null;
        }

        if (scheduleType === EXCEPTION) {
            validFrom = datetimeToISOString(formattedStartDate, startTime);
            validToDate = formattedEndDate;
            validTo = datetimeToISOString(validToDate, endTime);
            validToTime = endTime;
            validFromTime = startTime;
            formattedEndTime = '00:00';
            formattedStartTime = '00:00';
        }

        const response = await apiMethod(apiEndpoint, {
            title,
            status,
            quantity: Number(quantity) /* eslint-disable camelcase */,
            valid_to: validTo,
            valid_from: validFrom,
            valid_to_time: validToTime,
            valid_from_time: validFromTime,
            valid_to_date: validToDate,
            valid_from_date: formattedStartDate,
            schedule_type: scheduleType,
            end_time: formattedEndTime,
            start_time: formattedStartTime,
            ...(scheduleType === CONTINUOUS
                ? {
                      continuous_start_dow: toWeekday(startDate),
                      continuous_end_dow: toWeekday(endDate),
                  }
                : {
                      continuous_start_dow: null,
                      continuous_end_dow: null,
                  }),
            ...transformDaysOfWeek(daysOfWeek) /* eslint-enable camelcase */,
        });

        const formattedResponse = formatAddInventoryExceptionResponse(
            response?.data?.data
        );

        return formattedResponse;
    } catch (err) {
        sendSentryException(err);
        catchApiResponse(err);
    }
};

export default addInventoryException;
